import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, MoreHorizontal, FileText, Download } from 'lucide-react';
type TabType = 'all' | 'drafts' | 'sent' | 'due' | 'paid' | 'overdue';
const invoices = [{
  id: 'INV-2026-041',
  counterparty: 'The Empty Bottle',
  linked: 'Jun 12 Show',
  issue: 'Jun 01, 2026',
  due: 'Jun 12, 2026',
  amount: '$1,500.00',
  collected: '$0.00',
  status: 'Sent',
  method: 'Bank Transfer'
}, {
  id: 'INV-2026-040',
  counterparty: 'Beachland Tavern',
  linked: 'Jun 14 Show',
  issue: 'May 28, 2026',
  due: 'Jun 14, 2026',
  amount: '$1,200.00',
  collected: '$600.00',
  status: 'Partial',
  method: 'Credit Card'
}, {
  id: 'INV-2026-039',
  counterparty: 'Higher Ground',
  linked: 'Jun 18 Show',
  issue: 'May 15, 2026',
  due: 'May 30, 2026',
  amount: '$2,000.00',
  collected: '$0.00',
  status: 'Overdue',
  method: 'Bank Transfer'
}, {
  id: 'INV-2026-038',
  counterparty: 'The Sinclair',
  linked: 'May 20 Show',
  issue: 'May 01, 2026',
  due: 'May 20, 2026',
  amount: '$2,500.00',
  collected: '$2,500.00',
  status: 'Paid',
  method: 'Check'
}, {
  id: 'INV-2026-042',
  counterparty: 'Bowery Ballroom',
  linked: 'Jun 24 Show',
  issue: '-',
  due: '-',
  amount: '$3,000.00',
  collected: '$0.00',
  status: 'Draft',
  method: '-'
}];
export function InvoicesPage() {
  const [activeTab, setActiveTab] = useState<TabType>('all');
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="max-w-7xl mx-auto" data-id="element-3681">
      <div className="flex items-center justify-between mb-8" data-id="element-3682">
        <div data-id="element-3683">
          <h2 className="text-2xl font-bold mb-1" data-id="element-3684">Invoices</h2>
          <p className="text-sm text-textMuted" data-id="element-3685">
            Manage billing, deposits, and settlements.
          </p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-primary text-background rounded-lg text-sm font-medium hover:bg-primaryHover transition-colors" data-id="element-3686">
          <Plus className="w-4 h-4" data-id="element-3687" />
          Create Invoice
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-border mb-6 overflow-x-auto" data-id="element-3688">
        {(['all', 'drafts', 'sent', 'due', 'paid', 'overdue'] as TabType[]).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-sm font-medium transition-colors whitespace-nowrap capitalize ${activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-textMuted hover:text-textMain'}`} data-id="element-3689">
              {tab === 'all' ? 'All Invoices' : tab}
            </button>)}
      </div>

      {/* Filters & Table */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden" data-id="element-3690">
        <div className="p-4 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-3691">
          <div className="relative w-full sm:w-64" data-id="element-3692">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textMuted" data-id="element-3693" />
            <input type="text" placeholder="Search invoices..." className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:border-primary" data-id="element-3694" />
          </div>
          <div className="flex items-center gap-2" data-id="element-3695">
            <button className="px-4 py-2 bg-background border border-border rounded-lg text-sm font-medium hover:text-textMain transition-colors flex items-center gap-2" data-id="element-3696">
              <Filter className="w-4 h-4" data-id="element-3697" /> Filter
            </button>
            <button className="px-4 py-2 bg-background border border-border rounded-lg text-sm font-medium hover:text-textMain transition-colors flex items-center gap-2" data-id="element-3698">
              <Download className="w-4 h-4" data-id="element-3699" /> Export
            </button>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-3700">
          <table className="w-full text-sm text-left" data-id="element-3701">
            <thead className="text-xs text-textMuted uppercase bg-background/50 border-b border-border" data-id="element-3702">
              <tr data-id="element-3703">
                <th className="px-6 py-4 font-medium" data-id="element-3704">Invoice ID</th>
                <th className="px-6 py-4 font-medium" data-id="element-3705">Counterparty</th>
                <th className="px-6 py-4 font-medium" data-id="element-3706">Linked Show</th>
                <th className="px-6 py-4 font-medium" data-id="element-3707">Dates</th>
                <th className="px-6 py-4 font-medium" data-id="element-3708">Amount</th>
                <th className="px-6 py-4 font-medium" data-id="element-3709">Status</th>
                <th className="px-6 py-4 font-medium text-right" data-id="element-3710">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border" data-id="element-3711">
              {invoices.map((invoice, i) => <tr key={i} className="hover:bg-surfaceHover/50 transition-colors cursor-pointer" data-id="element-3712">
                  <td className="px-6 py-4 font-medium flex items-center gap-2" data-id="element-3713">
                    <FileText className="w-4 h-4 text-textMuted" data-id="element-3714" />
                    {invoice.id}
                  </td>
                  <td className="px-6 py-4 font-medium" data-id="element-3715">
                    {invoice.counterparty}
                  </td>
                  <td className="px-6 py-4 text-textMuted" data-id="element-3716">{invoice.linked}</td>
                  <td className="px-6 py-4" data-id="element-3717">
                    <div className="text-xs text-textMuted" data-id="element-3718">
                      Issued: {invoice.issue}
                    </div>
                    <div className="text-xs font-medium" data-id="element-3719">
                      Due: {invoice.due}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-3720">
                    <div className="font-medium" data-id="element-3721">{invoice.amount}</div>
                    <div className="text-xs text-textMuted" data-id="element-3722">
                      Collected: {invoice.collected}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-3723">
                    <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${invoice.status === 'Paid' ? 'bg-green-500/10 text-green-500' : invoice.status === 'Overdue' ? 'bg-red-500/10 text-red-500' : invoice.status === 'Partial' ? 'bg-amber-500/10 text-amber-500' : invoice.status === 'Sent' ? 'bg-blue-500/10 text-blue-500' : 'bg-surfaceHover text-textMuted'}`} data-id="element-3724">
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-3725">
                    <button className="p-1 text-textMuted hover:text-textMain transition-colors rounded hover:bg-background" data-id="element-3726">
                      <MoreHorizontal className="w-4 h-4" data-id="element-3727" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>;
}